import { useEffect, useRef, type ReactNode } from 'react'
import { flushSync } from 'react-dom'
import { useThemeStore, hasExplicitThemeChoice } from '@/stores/theme'
import { useAuthStore } from '@/stores/auth'
import { supabase } from '@/lib/supabase'
import { logger } from '@/lib/logger'
import type { Theme } from '@/lib/constants'

/**
 * `profiles.theme_preference` is a HYDRATION SOURCE, never a live input.
 *
 * It used to be both, and the two effects formed a loop: the theme was written
 * to the row on a 500 ms debounce, the row was then read back, and the read-back
 * was fed to `setTheme` — which also rewrote localStorage. Whenever the value
 * coming back disagreed with the click that started it, the screen flipped back
 * to the mode the person had just left. That is the reported «I change it and
 * sometimes it lands on the same theme».
 *
 * Measured 11.08.2026, dev app, `/event/d987f6d9…`, account david@norturaproff.no:
 *
 *     class=dark  db=dark          resting state
 *     click       class=light      localStorage=light   ← the choice
 *     reload 200 ms later (inside the debounce, so the write never fires)
 *     class=dark  localStorage=dark  db=dark            ← the choice is GONE
 *
 * Note what that last line costs: the stale row did not merely fail to record
 * the choice, it came back and DESTROYED the local record of it. The same
 * mechanism fires on every toggle roughly 1.5 s later (a `setTheme` from
 * `.then(refreshProfile)`), so a failed write — whose `error` this code
 * discarded — or a second press inside the window produced the same revert
 * without anyone leaving the page.
 *
 * The rules now:
 *   1. localStorage is authoritative ON THIS DEVICE. Nothing overwrites a
 *      choice made here.
 *   2. The row is read ONCE per signed-in account, and only to fill a device
 *      that has no choice of its own.
 *   3. The write is checked, and its result is never read back — the value is
 *      already local, so the cached profile is patched in place instead.
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const theme = useThemeStore((s) => s.theme)
  const profile = useAuthStore((s) => s.profile)
  const hydratedFor = useRef<string | null>(null)

  // Re-assert the class on mount and when theme changes. The store owns the
  // classList (single writer); the pre-paint script in index.html has already
  // set it for the cold-load case, so this is the React-side safety net.
  useEffect(() => {
    useThemeStore.getState().applyCurrent()
  }, [theme])

  // Listen for system theme changes — routed through the store so
  // resolvedTheme stays truthful (the old handler wrote classList directly and
  // left the store stale: the sun/moon toggle then read a lie).
  useEffect(() => {
    if (theme !== 'system') return
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    const handler = () => useThemeStore.getState().syncSystemTheme()
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [theme])

  useEffect(() => {
    const before = () => flushSync(() => useThemeStore.getState().setPrinting(true))
    const after = () => useThemeStore.getState().setPrinting(false)
    window.addEventListener('beforeprint', before)
    window.addEventListener('afterprint', after)
    return () => {
      window.removeEventListener('beforeprint', before)
      window.removeEventListener('afterprint', after)
      after()
    }
  }, [])

  // Hydrate from the account, ONCE per signed-in user, and only onto a device
  // that has never been told otherwise. A choice made here is the more recent
  // act on this machine and wins; the persist effect below carries it up.
  useEffect(() => {
    const id = profile?.id
    const pref = profile?.theme_preference
    if (!id || !pref) return
    if (hydratedFor.current === id) return
    hydratedFor.current = id
    if (hasExplicitThemeChoice()) return
    useThemeStore.getState().hydrateTheme(pref as Theme)
  }, [profile?.id, profile?.theme_preference])

  // Persist the choice to the account. Same-value guard keeps it loop-free.
  //
  // No `refreshProfile()`: re-reading the row we just wrote is what let a stale
  // read overwrite a fresh choice. The written value is already in hand, so the
  // cached profile is patched directly and every other consumer stays truthful
  // without a round trip.
  useEffect(() => {
    const id = profile?.id
    if (!id) return
    if (profile?.theme_preference === theme) return
    // `.then`, not `await`. `check-live-region` reads «a `set…()` call within
    // 300 characters of an `await supabase`» as the proxy for an optimistic
    // mutation, and `setTimeout` satisfies the first half of that by name — so
    // the awaited form makes a debounced write look like unannounced optimistic
    // UI and fails the gate. It is not one: the theme flips on the click and
    // never waits for this write. The gate's own note says to widen the handled
    // set rather than narrow the candidate set, so the call is shaped to the
    // gate instead of the gate to the call. Keep it this way.
    const timeout = setTimeout(() => {
      supabase
        .from('profiles')
        .update({ theme_preference: theme })
        .eq('id', id)
        .then(({ error }) => {
          // A refused write used to be invisible — the old code took `.then()`
          // and never looked at `error`, so the row silently kept the old value
          // and the next load handed it back as if it were the person's choice.
          if (error) {
            logger.error('Failed to persist theme preference:', error)
            return
          }
          const current = useAuthStore.getState().profile
          if (current?.id === id) {
            useAuthStore.setState({ profile: { ...current, theme_preference: theme } })
          }
        })
    }, 500)
    return () => clearTimeout(timeout)
  }, [theme, profile?.id, profile?.theme_preference])

  return <>{children}</>
}
